import { useState, useRef, useEffect } from "react";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db, storage } from "../firebase";
import { emailBoasVindas } from "../utils/email";
import { enviarCodigoConfirmacao } from "../utils/printControlAuth";
import { AuthLayout, PasswordField, GoogleMark } from "./AuthLayout";
import { CadastroOtpAnim } from "./CadastroOtpAnim";
import { normalizarCelularBR } from "../utils/telefone";
import { senhaForte, MENSAGEM_SENHA_FRACA } from "../utils/senha";
import { RequisitosSenha } from "./RequisitosSenha";

const functions = getFunctions(undefined, "us-central1");
const solicitarVerificacaoEmail = httpsCallable(functions, "solicitarVerificacaoEmail");
const confirmarVerificacaoEmail = httpsCallable(functions, "confirmarVerificacaoEmail");


// Etapas do cadastro com e-mail/senha. "contato": e-mail + WhatsApp (opcional);
// "codigo": o código do e-mail. Pelo Google o e-mail já vem verificado e não há
// senha própria: depois do contato a conta já é criada. O WhatsApp é só
// guardado, sem código — a confirmação vem depois, para contas novas e antigas.
const ETAPAS_EMAIL  = ["dados", "contato", "codigo", "senha"];
const ETAPAS_GOOGLE = ["dados", "contato"];
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Cadastro({ onVoltar, onCadastrado, googleUser }) {
  const [vals,    setVals]    = useState({ nome: googleUser?.displayName || "", empresa:"", email: googleUser?.email || "", telefone:"", senha:"", confirmar:"" });
  // Código do e-mail: para qual e-mail já saiu e qual foi confirmado
  const [enviado, setEnviado] = useState("");
  const [confirmado, setConfirmado] = useState("");
  const [verSenha,setVerSenha]= useState({ senha:false, confirmar:false });
  const [logoFile,setLogoFile]= useState(null);
  const [logoPreview,setLogoPreview] = useState(null);
  const [erro,    setErro]    = useState("");
  const [loading, setLoading] = useState(false);
  const [aceite,  setAceite]  = useState(false);
  const fileRef = useRef();

  // Cadastro por e-mail passa por 4 etapas (dados -> email -> código ->
  // senha); via Google só tem "dados" (e-mail já vem verificado e não usa
  // senha própria) — mas o passo "dados" em si é configurado do mesmo jeito
  // pros dois casos.
  const etapas = googleUser ? ETAPAS_GOOGLE : ETAPAS_EMAIL;
  const [etapa, setEtapa] = useState("dados");
  const etapaIdx = etapas.indexOf(etapa);
  const emailNorm = vals.email.trim().toLowerCase();
  const numero = normalizarCelularBR(vals.telefone);
  const emailOk = !!googleUser || (!!emailNorm && confirmado === emailNorm);
  const codigosOk = etapa === "codigo" && emailOk;

  useEffect(() => () => { if (logoPreview) URL.revokeObjectURL(logoPreview); }, [logoPreview]);

  const set = (k,v) => setVals(p=>({...p,[k]:v}));

  const escolherLogo = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return setErro("Selecione um arquivo de imagem.");
    setLogoFile(f);
    setLogoPreview(URL.createObjectURL(f));
    setErro("");
  };

  const erroMsg = (code) => ({
    "auth/email-already-in-use": "Este e-mail já está em uso.",
    "auth/invalid-email":        "E-mail inválido.",
    "auth/weak-password":        "Senha muito fraca.",
    "auth/network-request-failed":"Sem conexão com a internet.",
  }[code] || "Erro ao criar conta. Tente novamente.");

  const erroFn = (e) => ({
    "already-exists":     "Este e-mail já está em uso.",
    "invalid-argument":   e.message?.replace(/^.*?:\s*/, "") || "Dados inválidos.",
    "not-found":          "Código expirado. Solicite um novo.",
    "deadline-exceeded":  "Código expirado. Solicite um novo.",
  }[e.code?.replace("functions/","")] || "Erro ao verificar e-mail. Tente novamente.");

  // Etapa 1 (igual pros dois fluxos): nome, empresa, logo opcional, aceite.
  const avancarDados = () => {
    setErro("");
    if (!vals.nome.trim())    return setErro("Informe seu nome.");
    if (!vals.empresa.trim()) return setErro("Informe o nome da empresa.");
    if (!aceite) return setErro("Você precisa aceitar a Política de Privacidade para continuar.");
    setEtapa("contato");
  };

  // Etapa 2: e-mail (código) + WhatsApp (opcional, sem código). O código que já
  // saiu (ou já foi confirmado) para o mesmo e-mail não sai de novo. Pelo
  // Google não há código: a conta já é criada aqui.
  const avancarContato = async () => {
    setErro("");
    if (!googleUser && !EMAIL_REGEX.test(vals.email.trim())) return setErro("Informe um e-mail válido.");
    if (vals.telefone.trim() && !numero) return setErro("Confira o WhatsApp: celular com DDD, por exemplo (73) 99999-0000. Se preferir, deixe em branco.");
    if (googleUser) return finalizarCadastro();
    if (enviado !== emailNorm && confirmado !== emailNorm) {
      setLoading(true);
      try { await solicitarVerificacaoEmail({ email: vals.email.trim() }); }
      catch (e) { setErro(erroFn(e)); return; }
      finally { setLoading(false); }
      setEnviado(emailNorm);
    }
    avancouRef.current = false;
    setEtapa("codigo");
  };

  // Etapa 3: o código do e-mail (CadastroOtpAnim cuida da UI; aqui só as chamadas)
  const verificarCodigo = async (code) => {
    await confirmarVerificacaoEmail({ email: vals.email.trim(), code });
    setConfirmado(emailNorm);
  };
  const reenviarCodigo = async () => {
    await solicitarVerificacaoEmail({ email: vals.email.trim() });
  };

  // Com o e-mail confirmado, segue sozinho (ou no botão) para a senha
  const avancouRef = useRef(false);
  const concluirCodigos = () => {
    if (avancouRef.current) return;
    avancouRef.current = true;
    setEtapa("senha");
  };
  const concluirRef = useRef(concluirCodigos);
  concluirRef.current = concluirCodigos;
  useEffect(() => {
    if (!codigosOk) return;
    const t = setTimeout(() => concluirRef.current(), 1800);
    return () => clearTimeout(t);
  }, [codigosOk]);

  // Etapa 4: senha -> cria a conta de fato.
  const avancarSenha = () => {
    setErro("");
    if (!senhaForte(vals.senha)) return setErro(MENSAGEM_SENHA_FRACA);
    if (vals.senha !== vals.confirmar) return setErro("As senhas não coincidem.");
    finalizarCadastro();
  };

  // Cria a conta de fato — chamada no fim do fluxo (senha, ou direto pro
  // Google logo depois de "contato").
  const finalizarCadastro = async () => {
    setErro("");
    setLoading(true);
    try {
      const uid = googleUser ? googleUser.uid : (await createUserWithEmailAndPassword(auth, vals.email.trim(), vals.senha)).user.uid;
      // O código da etapa anterior já provou o e-mail: o servidor marca a conta
      // nova como confirmada (junto com o resto do cadastro), e ela entra direto
      // nos planos, sem a tela "Confirme seu e-mail". Se falhar, aquela tela
      // resolve depois, como no login.
      const confirmacao = googleUser ? null : enviarCodigoConfirmacao(auth).catch(() => null);
      const emailFinal = googleUser ? googleUser.email : vals.email.trim();
      if (!googleUser) await updateProfile(auth.currentUser, { displayName: vals.nome.trim() });

      let logoUrl = "";
      if (logoFile) {
        const logoRef = ref(storage, `users/${uid}/logo`);
        await uploadBytes(logoRef, logoFile);
        logoUrl = await getDownloadURL(logoRef);
      }

      // merge: a conta do Google já tem um documento mínimo, criado no
      // primeiro login (ver Login.js). `plan` só vai no cadastro por e-mail —
      // no Google ele já está como "pending" e reescrever plano é justamente
      // o que a regra do Firestore bloqueia num update.
      await setDoc(doc(db, "users", uid), {
        nome:    vals.nome.trim(),
        empresa: vals.empresa.trim(),
        email:   emailFinal,
        logoUrl,
        // WhatsApp opcional e ainda sem código: só o número (+55…)
        ...(numero ? { telefone: numero } : {}),
        ...(googleUser ? {} : { plan: "pending" }),
        cadastroCompleto: true,
        createdAt: serverTimestamp(),
        privacyAcceptedAt: serverTimestamp(),
      }, { merge: true });

      // Os exemplos são criados somente após ativar trial/assinatura.

      emailBoasVindas(vals.nome.trim(), emailFinal);
      await confirmacao;
      onCadastrado({ nome: vals.nome.trim(), empresa: vals.empresa.trim(), logoUrl, uid, email: emailFinal, avatar:"🧑‍💻" }, false);
    } catch(e) {
      setErro(erroMsg(e.code));
      avancouRef.current = false;
    }
    setLoading(false);
  };

  const titulos = { dados: googleUser ? "Complete seu cadastro" : "Crie sua conta", contato: "Como falar com você?", codigo: "Confirme seu e-mail", senha: "Proteja sua conta" };
  const descricoes = { dados: "Vamos preparar o espaço da sua empresa. É rápido e a logo é opcional.", contato: googleUser ? "Seu e-mail já está confirmado pelo Google. Se quiser, deixe também seu WhatsApp." : "Informe seu e-mail para receber o código de confirmação. O WhatsApp é opcional.", codigo: "Digite o código recebido. Assim que confirmar, seguimos para a senha.", senha: "Último passo! Escolha uma senha para acessar seu Print Control." };
  const nomesEtapas = { dados: "Empresa", contato: "Contato", codigo: "Código", senha: "Senha" };
  const erroBox = erro && <div className="auth-feedback" role="alert">{erro}</div>;
  const voltar = (destino) => { setEtapa(destino); setErro(""); };
  const submit = (fn) => (e) => { e.preventDefault(); if (!loading) fn(); };

  return <AuthLayout title={titulos[etapa]} subtitle={descricoes[etapa]} footer={etapa === "dados" && <button type="button" className="auth-link" onClick={onVoltar}>← Voltar para o login</button>}>
    <ol className="auth-progress" aria-label="Etapas do cadastro">
      {etapas.map((passo, i) => <li key={passo} className={i <= etapaIdx ? "is-active" : ""} aria-current={i === etapaIdx ? "step" : undefined}><span>{i < etapaIdx ? "✓" : i + 1}</span>{nomesEtapas[passo]}</li>)}
    </ol>
    {googleUser && (etapa === "dados" || etapa === "contato") && <div className="auth-notice auth-google-account"><GoogleMark /><div><strong>Conectado com Google</strong><span>{googleUser.email}</span><small>E-mail já verificado</small></div></div>}

    {etapa === "dados" && <form className="auth-form" onSubmit={submit(avancarDados)} noValidate>
      <div className="auth-logo-upload">
        <button type="button" className="auth-logo-preview" onClick={() => fileRef.current.click()} aria-label={logoPreview ? "Trocar logo da empresa" : "Enviar logo da empresa"}>{logoPreview ? <img src={logoPreview} alt="Logo da sua empresa" /> : <span aria-hidden="true">🖼️</span>}</button>
        <div><strong>Logo da empresa <small>Opcional</small></strong><button type="button" className="auth-link" onClick={() => fileRef.current.click()}>{logoPreview ? "Trocar imagem" : "Escolher imagem"}</button><p>PNG, JPG ou SVG</p></div>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={escolherLogo} />
      </div>
      {[{id:"nome",label:"Seu nome",placeholder:"João Silva",complete:"name"},{id:"empresa",label:"Nome da empresa",placeholder:"Minha Gráfica 3D",complete:"organization"}].map(({id,label,placeholder,complete}) => <div className="auth-field" key={id}><label htmlFor={"cadastro-" + id}>{label}</label><input id={"cadastro-" + id} autoComplete={complete} placeholder={placeholder} value={vals[id]} onChange={e => set(id,e.target.value)} required /></div>)}
      <label className="auth-consent"><input type="checkbox" checked={aceite} onChange={e => setAceite(e.target.checked)} /><span>Li e aceito a <a href="https://politica-termos.netlify.app/privacy-policy" target="_blank" rel="noreferrer">Política de Privacidade</a> do Print Control.</span></label>
      {erroBox}<button className="auth-submit" type="submit">PRÓXIMO →</button>
    </form>}

    {etapa === "contato" && <form onSubmit={submit(avancarContato)} noValidate><fieldset className="auth-form auth-fieldset" disabled={loading}>
      {!googleUser && <div className="auth-field"><label htmlFor="cadastro-email">E-mail</label><input id="cadastro-email" type="email" aria-label="E-MAIL" autoComplete="email" placeholder="seu@email.com" value={vals.email} onChange={e => set("email",e.target.value)} required /></div>}
      <div className="auth-field"><label htmlFor="cadastro-telefone">WhatsApp (celular, opcional)</label><input id="cadastro-telefone" type="tel" inputMode="tel" autoComplete="tel" aria-label="WHATSAPP (CELULAR)" placeholder="(73) 99999-0000" value={vals.telefone} onChange={e => set("telefone",e.target.value)} /><small className="auth-help">Com DDD. Dá para informar ou trocar depois em Conta.</small></div>
      {erroBox}<button type="submit" className="auth-submit" disabled={loading}>{loading ? (googleUser ? "CRIANDO CONTA..." : "ENVIANDO...") : googleUser ? "CRIAR CONTA →" : "ENVIAR CÓDIGO →"}</button>
      <button type="button" className="auth-link" onClick={() => voltar("dados")}>← Voltar</button>
    </fieldset></form>}

    {etapa === "codigo" && <div className="auth-form auth-verification" aria-busy={loading}>
      <CadastroOtpAnim key={"email:" + emailNorm} email={vals.email.trim()} jaConfirmado={emailOk} onVerify={verificarCodigo} onResend={reenviarCodigo} />
      {erroBox}<button type="button" className="auth-submit" onClick={concluirCodigos} disabled={!codigosOk || loading}>{codigosOk ? "CONTINUAR →" : "CONFIRME O CÓDIGO"}</button>
      <button type="button" className="auth-link" disabled={loading} onClick={() => voltar("contato")}>← Corrigir e-mail ou WhatsApp</button>
    </div>}

    {etapa === "senha" && <form onSubmit={submit(avancarSenha)} noValidate><fieldset className="auth-form auth-fieldset" disabled={loading}>
      <PasswordField id="cadastro-senha" label="Senha" value={vals.senha} onChange={e => set("senha",e.target.value)} visible={verSenha.senha} onToggle={() => setVerSenha(v => ({...v,senha:!v.senha}))} autoComplete="new-password" placeholder="Mín. 8 caracteres" />
      <RequisitosSenha senha={vals.senha} className="auth-password-rules" />
      <PasswordField id="cadastro-confirmar" label="Confirmar senha" value={vals.confirmar} onChange={e => set("confirmar",e.target.value)} visible={verSenha.confirmar} onToggle={() => setVerSenha(v => ({...v,confirmar:!v.confirmar}))} autoComplete="new-password" placeholder="••••••••" />
      {erroBox}<button type="submit" className="auth-submit" disabled={loading}>{loading ? "CRIANDO CONTA..." : "CRIAR CONTA →"}</button>
    </fieldset></form>}
  </AuthLayout>;
}
